import { createPortal } from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  XIcon,
  Upload,
  FileText,
  Download,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import { useImportRosterMutation } from "../../api/endpoints/roster";
import type { Clubhouse_LeagueRosterImportResultDto } from "../../types/leagueRoster";
import { extractServerErrorLines } from "../helpers";
import { downloadRosterTemplate, countCsvDataRows } from "../rosterCsv";
import { OpenRoundCompletionNotice } from "../common/OpenRoundCompletionNotice";

interface ImportRosterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueId: string;
  /** League's home facility; falls back to the current facility when omitted. */
  facilityId?: string;
  onImported?: () => void;
  /**
   * Shown above Import while the current round is still open, before the
   * file is committed.
   */
  openRoundCompletionNotice?: string | null;
}

export function ImportRosterDialog({
  open,
  onOpenChange,
  leagueId,
  facilityId: facilityIdProp,
  onImported,
  openRoundCompletionNotice,
}: ImportRosterDialogProps) {
  const currentFacilityId = useGetCurrentLocalFacilityId();
  const facilityId = facilityIdProp ?? currentFacilityId;
  const [importRoster] = useImportRosterMutation();

  const [file, setFile] = useState<File | null>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] =
    useState<Clubhouse_LeagueRosterImportResultDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setRowCount(null);
      setIsDragging(false);
      setIsUploading(false);
      setResult(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  const pickFile = useCallback((f: File) => {
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setError("Only CSV files are accepted.");
      return;
    }
    setError(null);
    setResult(null);
    setFile(f);
    setRowCount(null);
    void countCsvDataRows(f).then(setRowCount);
  }, []);

  async function handleImport() {
    if (!file || !leagueId || !facilityId) return;
    setError(null);
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await importRoster({
        facilityId,
        leagueId,
        body: formData,
      }).unwrap();
      setResult(data);
      onImported?.();
    } catch (err) {
      // Surface the server's own validation message when it provides one, else
      // a friendly fallback.
      const serverMessage = extractServerErrorLines(err).join("\n");
      setError(
        serverMessage || "Upload failed. Please check your file and try again.",
      );
    } finally {
      setIsUploading(false);
    }
  }

  function importAnother() {
    setFile(null);
    setRowCount(null);
    setResult(null);
    setError(null);
  }

  if (!open) return null;

  const {
    totalRows = 0,
    successful = 0,
    failed = 0,
    errors = [],
  } = result ?? {};

  return createPortal(
    <div className="fixed inset-0 z-[200] grid place-items-center">
      <button
        type="button"
        aria-label="Dismiss dialog"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Import Roster"
        className="relative z-10 flex w-full max-w-lg max-h-[90vh] flex-col rounded-2xl bg-popover text-sm text-popover-foreground shadow-xl motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-2 motion-safe:duration-200"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-2 right-2 inline-flex size-9 items-center justify-center rounded-full bg-primary/6 text-primary transition-colors hover:bg-primary/12"
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 pt-6 pb-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Upload className="size-4" />
            </span>
            <h2 className="poppins text-lg font-bold leading-none tracking-tight">
              Import Roster
            </h2>
          </div>

          <p className="text-sm text-text-muted">
            CSV needs 12 columns — username, first/last name, email, country
            code, phone, zip, date of birth, gender, handedness, handicap, tee.
            Most can be left blank and completed later from the roster list.
          </p>

          <button
            type="button"
            onClick={downloadRosterTemplate}
            className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
          >
            <Download className="size-3.5" />
            Download the CSV template (12 columns)
          </button>

          {file ? (
            <div className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/[0.03] px-4 py-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <FileText className="size-4 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="poppins truncate text-sm font-semibold text-text-primary">
                  {file.name}
                </p>
                {rowCount != null && (
                  <p className="text-xs text-text-muted">
                    {rowCount} row{rowCount === 1 ? "" : "s"}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="shrink-0 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
              >
                Replace file
              </button>
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              aria-label="Upload CSV file"
              className={cn(
                "flex cursor-pointer select-none flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors",
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40 hover:bg-surface/50",
              )}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  fileInputRef.current?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const dropped = e.dataTransfer.files[0];
                if (dropped) pickFile(dropped);
              }}
            >
              <span
                className={cn(
                  "flex size-10 items-center justify-center rounded-full transition-colors",
                  isDragging ? "bg-primary/15" : "bg-muted",
                )}
              >
                <Upload
                  className={cn(
                    "size-5",
                    isDragging ? "text-primary" : "text-text-muted",
                  )}
                />
              </span>
              <p className="text-sm font-medium text-text-primary">
                Drop your CSV here, or{" "}
                <span className="font-semibold text-primary">browse</span>
              </p>
              <p className="text-xs text-text-muted">CSV files only</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="sr-only"
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) pickFile(picked);
              // Reset so re-selecting the same file (e.g. after Import More)
              // still fires onChange.
              e.target.value = "";
            }}
          />

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/8 px-4 py-3 text-sm text-destructive">
              <AlertCircle className="size-4 shrink-0" />
              {error}
            </div>
          )}

          {result && (
            <div className="flex flex-col gap-2 rounded-xl border border-border bg-surface/50 px-4 py-3">
              <p className="poppins text-sm font-semibold text-text-primary">
                {successful} of {totalRows} player{totalRows === 1 ? "" : "s"}{" "}
                added
              </p>
              {failed > 0 ? (
                <>
                  <p className="text-xs text-text-muted">
                    {failed} row{failed === 1 ? "" : "s"} failed — fix and
                    re-import just those rows, no need to redo the whole file.
                  </p>
                  <ol className="flex list-inside list-decimal flex-col gap-1 text-sm text-text-primary">
                    {errors.map((e, i) => (
                      <li key={i}>
                        <span className="font-semibold">
                          Row {e.row ?? "—"}
                        </span>{" "}
                        — {e.reason ?? "Unknown error"}
                      </li>
                    ))}
                  </ol>
                </>
              ) : (
                <p className="text-xs text-text-muted">
                  All rows imported successfully.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-3 border-t border-border px-6 py-4">
          {!result && (
            <OpenRoundCompletionNotice message={openRoundCompletionNotice} />
          )}
          <div className="flex justify-end gap-2">
            {result ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={importAnother}
                  className="rounded-md font-bold text-primary"
                >
                  Import More
                </Button>
                <Button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="rounded-md bg-primary font-bold text-white"
                >
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isUploading}
                  className="rounded-md font-bold text-primary"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleImport()}
                  disabled={!file || isUploading}
                  className="group/import min-w-[120px] rounded-md bg-primary font-bold text-white motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:-translate-y-px active:scale-[0.97]"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Importing…
                    </>
                  ) : (
                    <>
                      <Upload
                        data-icon="inline-start"
                        className="motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out motion-safe:group-hover/import:-translate-y-0.5"
                      />
                      Import Roster
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
